import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import ProjectBox from "../Elements/ProjectBox";
import AddImage2 from "../../assets/img/about.png";
import NavaService from "../../services/NavaService";
import { useNavigate } from "react-router-dom";

export default function Projects() {
  const [properties, setProperties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      // landing page only teases a handful of listings; the full catalog
      // with county filter + search lives on /properties
      const res = await NavaService.getProperties({ limit: 9 });
      setProperties(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch properties", err);
      setProperties([]);
    }
  };

  const getRequestedSlidesPerView = useCallback(() => {
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 1024) return 2;
    return 3;
  }, []);

  const [slidesPerView, setSlidesPerView] = useState(getRequestedSlidesPerView());
  const [index, setIndex] = useState(0);

  const timerRef = useRef(null);
  const propertiesLenRef = useRef(0);
  const effectiveSlidesRef = useRef(1);
  const maxIndexRef = useRef(0);

  useEffect(() => { propertiesLenRef.current = properties.length; }, [properties.length]);
  useEffect(() => { effectiveSlidesRef.current = Math.min(Math.max(1, slidesPerView), Math.max(1, propertiesLenRef.current)); }, [slidesPerView, properties.length]);
  useEffect(() => {
    maxIndexRef.current = Math.max(0, Math.ceil(propertiesLenRef.current / effectiveSlidesRef.current) - 1);
  }, [properties.length, slidesPerView]);

  useEffect(() => {
    const onResize = () => setSlidesPerView(getRequestedSlidesPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getRequestedSlidesPerView]);

  const delayMs = 3000;

  const stopAutoplay = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();

    if (propertiesLenRef.current <= effectiveSlidesRef.current) return;

    const tick = () => {
      setIndex((cur) => {
        const mi = maxIndexRef.current;
        return cur >= mi ? 0 : cur + 1;
      });
      timerRef.current = setTimeout(tick, delayMs);
    };

    timerRef.current = setTimeout(tick, delayMs);
  }, [stopAutoplay]);

  useEffect(() => {
    setIndex((cur) => (cur > maxIndexRef.current ? 0 : cur));

    startAutoplay();

    return () => {
      stopAutoplay();
    };
  }, [properties.length, slidesPerView, startAutoplay, stopAutoplay]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [startAutoplay, stopAutoplay]);

  const restartAutoplay = useCallback(() => {
    stopAutoplay();
    startAutoplay();
  }, [startAutoplay, stopAutoplay]);

  const nextSlide = useCallback(() => {
    if (propertiesLenRef.current <= effectiveSlidesRef.current) return;
    setIndex((cur) => (cur >= maxIndexRef.current ? 0 : cur + 1));
    restartAutoplay();
  }, [restartAutoplay]);

  const prevSlide = useCallback(() => {
    if (propertiesLenRef.current <= effectiveSlidesRef.current) return;
    setIndex((cur) => (cur <= 0 ? maxIndexRef.current : cur - 1));
    restartAutoplay();
  }, [restartAutoplay]);

  const effectiveSlides = Math.min(Math.max(1, slidesPerView), Math.max(1, properties.length));
  const translatePercent = properties.length > 0
    ? -(index * effectiveSlides * (100 / properties.length))
    : 0;

  return (
    <Wrapper id="projects">
      <div className="whiteBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Properties & Assets for Auction</h1>
            <p className="font13">
              Explore properties and assets currently available for auction through Sare Auctioneers.
            </p>
            <p className="font13">
              From land and residential properties to commercial assets, our listings provide key
              details to help you make informed decisions and bid with confidence.
            </p>

          </HeaderInfo>
          <div className="sliderWrap">
            <button className="ctrlBtn prevBtn" onClick={prevSlide} aria-label="Previous slide">
              ‹
            </button>

            <div className="sliderViewport">
              <div
                className="sliderTrack"
                style={{
                  transform: `translateX(${translatePercent}%)`,
                  transition: "transform 600ms ease"
                }}
              >
                {properties.map((p) => {
                  const propId = p.id;
                  const imgSrc = p.cover_image
                    ? NavaService.getPropertyImageUrl(propId, p.cover_image)
                    : AddImage2;
                  return (
                    <div
                      className="slide"
                      key={propId}
                      style={{ flex: `0 0 ${100 / effectiveSlides}%` }}
                    >
                      <div className="project-card" style={{ padding: "0 12px" }}>
                        <ProjectBox
                          img={imgSrc}
                          title={p.title_number}
                          text={<span>{p.location}</span>}
                          action={() => navigate(`/properties/${propId}`)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button className="ctrlBtn nextBtn" onClick={nextSlide} aria-label="Next slide">
              ›
            </button>
          </div>

          {properties.length === 0 && (
            <div className="row textCenter">
              <div className="col-xs-12">
                <p>No properties listed yet.</p>
              </div>
            </div>
          )}

          <div className="row flexCenter">
            <ViewAllWrapper>
              <ViewAllBtn onClick={() => navigate("/properties")} className="pointer radius8">
                View all properties
              </ViewAllBtn>
            </ViewAllWrapper>
          </div>
        </div>
      </div>

      <div className="lightBg">
        <div className="container">
          <Advertising className="flexSpaceCenter">
            <AddLeft>
              <AddLeftInner>
                <ImgWrapper className="flexCenter">
                  <img className="radius8" src={AddImage2} alt="add" />
                </ImgWrapper>
              </AddLeftInner>
            </AddLeft>
            <AddRight>
              <h4 className="font18 semiBold">A few words about us</h4>
              <h2 className="font40 extraBold">At Sare Auctioneers,</h2>
              <p className="font16">
                we are committed to providing professional, efficient, and reliable auctioneering
                and asset recovery services. Established in 2015 and licensed as a Class B auctioneer,
                we handle public auctions, property and asset realization, repossessions, debt collection,
                court process serving, and related services across Kenya and the wider East African region.
                With a strong foundation in integrity, accountability, professionalism, and customer care,
                we strive to deliver trusted solutions that meet the needs of our clients and partners.
              </p>
            </AddRight>
          </Advertising>
        </div>
      </div>
    </Wrapper>
  );
}

const HeaderInfo = styled.div`
  color: #3b1718;

  p { color: #5d191a; }

  @media (max-width: 860px) {
    text-align: center;
  }
`;

const ViewAllWrapper = styled.div`
  margin: 50px 0;
`;

const ViewAllBtn = styled.button`
  border: 1px solid #5d191a;
  background: transparent;
  color: #5d191a;
  padding: 12px 28px;
  font-weight: 700;
  &:hover {
    background: #5d191a;
    color: #fff;
  }
`;

const Wrapper = styled.section`
  width: 100%;
  color: #3b1718;

  .sliderWrap {
    position: relative;
  }

  .sliderViewport {
    width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    padding: 20px 0;
  }

  .sliderTrack {
    display: flex;
    align-items: stretch;
    will-change: transform;
  }

  .slide {
    box-sizing: border-box;
    display: flex;
    align-items: stretch;
  }

  .prevBtn {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 12;
  }

  .nextBtn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 12;
  }

  .ctrlBtn {
    background: #5d191a;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
  .ctrlBtn:active { transform: translateY(1px); }
  .ctrlBtn:focus { outline: 2px solid rgba(93,25,26,0.25); }

  @media (max-width: 640px) {
    .project-card {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .project-card img {
      width: auto;
      max-width: 80%;
      height: auto;
      margin: 0 auto 12px;
    }
    .slide { justify-content: center; }
    .prevBtn { left: 6px; }
    .nextBtn { right: 6px; }
    .ctrlBtn { padding: 6px 8px; font-size: 13px; }
  }
    .prevBtn { left: 6px; }
    .nextBtn { right: 6px; }
    .ctrlBtn { padding: 6px 8px; font-size: 13px; }
  }

  .project-card img {
    width: 100%;
    height: 220px;
    display: block;
    object-fit: cover;
    object-position: center;
    border-radius: 8px;
  }

  @media (max-width: 920px) {
    .project-card img { height: 180px; }
  }
  @media (max-width: 480px) {
    .project-card img { height: 140px; }
  }
`;

const Advertising = styled.div`
  padding: 100px 0;
  margin: 100px 0;
  position: relative;
  @media (max-width: 1160px) {
    padding: 60px 0 40px 0;
  }
  @media (max-width: 860px) {
    flex-direction: column;
    padding: 0 0 30px 0;
    margin: 80px 0 0px 0;
  }
`;
const AddLeft = styled.div`
  position: relative;
  width: 50%;
  p {
    max-width: 475px;
  }
  @media (max-width: 860px) {
    width: 80%;
    order: 2;
    text-align: center;
    h2 {
      line-height: 3rem;
      margin: 15px 0;
    }
    p {
      margin: 0 auto;
    }
  }
`;
const AddRight = styled.div`
  width: 50%;
  @media (max-width: 860px) {
    width: 80%;
    order: 2;
  }
`;
const AddLeftInner = styled.div`
  width: 100%;
  position: absolute;
  top: -300px;
  left: 0;
  @media (max-width: 1190px) {
    top: -250px;
  }
  @media (max-width: 920px) {
    top: -200px;
  }
  @media (max-width: 860px) {
    order: 1;
    position: relative;
    top: -60px;
    left: 0;
  }
`;
const ImgWrapper = styled.div`
  width: 100%;
  padding: 0 15%;

  .fixedImg {
    width: 100%;
    height: 200px;          
    display: block;
    object-fit: cover;      
    object-position: center; 
    border-radius: 8px;     
  }

  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 400px) {
    padding: 0;
  }
`;
